import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SubscriptionSection from '../components/SubscriptionSection';
import '../styles/NewsDetail.css';

const NewsDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [newsItem, setNewsItem] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API configuration
  const API_BASE_URL = "https://phucansolutions.vn" ;

  // Fetch news detail
  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 Fetching news detail for slug:', slug);
        
        // Fetch news by slug directly since backend now supports it
        const newsResponse = await fetch(`${API_BASE_URL}/api/news/${slug}`);

        if (!newsResponse.ok) {
          throw new Error('Không tìm thấy bài viết');
        }

        const newsData = await newsResponse.json();
        const processedNews = processNewsItem(newsData.data);
        setNewsItem(processedNews);
        
        // Fetch related news
        await fetchRelatedNews(processedNews.category);
        
      } catch (err) {
        console.error('❌ Error fetching news detail:', err);
        setError(err.message);
        
        // Use fallback data if API fails
        setNewsItem(getFallbackNewsItem(slug));
        setRelatedNews(getFallbackRelatedNews());
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchNewsDetail();
    }
  }, [slug, API_BASE_URL]);

  // Fetch related news
  const fetchRelatedNews = async (category) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/news?category=${category}&limit=3`);
      if (response.ok) {
        const data = await response.json();
        const processed = data.data?.map(processNewsItem).slice(0, 3) || [];
        setRelatedNews(processed);
      }
    } catch (err) {
      console.error('Error fetching related news:', err);
      setRelatedNews(getFallbackRelatedNews());
    }
  };

  // Process news item
  const processNewsItem = (news) => {
    if (!news) return null;
    
    return {
      id: news.id,
      title: news.title,
      content: news.content || news.description || '',
      excerpt: news.excerpt || news.description?.substring(0, 200) + '...',
      image: news.image || '/images/placeholder-news.svg',
      category: news.category || 'Tin tức',
      author: news.author || 'PhucAn Solutions',
      date: formatDate(news.createdAt || news.date),
      views: news.views || Math.floor(Math.random() * 3000) + 500,
      slug: news.slug || createSlug(news.title)
    };
  };

  // Create slug from title
  const createSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[đĐ]/g, 'd')
      .replace(/[^a-z0-9\s]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath === '/images/placeholder-news.svg') {
      return '/images/placeholder-news.svg';
    }
    
    if (imagePath.startsWith('/uploads/')) {
      return `${API_BASE_URL}${imagePath}`;
    }
    
    return imagePath;
  };

  // Fallback data
  const getFallbackNewsItem = (slug) => ({
    id: 1,
    title: 'Công ty PhucAn Solutions đạt giải thưởng "Doanh nghiệp tiêu biểu 2024"',
    content: `
      <p>Ngày 15 tháng 12 năm 2024, tại Lễ tôn vinh "Doanh nghiệp tiêu biểu 2024" do Hiệp hội Doanh nghiệp Công nghệ Việt Nam tổ chức, Công ty TNHH Giải pháp PhucAn (PhucAn Solutions) đã vinh dự nhận giải thưởng danh giá này.</p>
      
      <p>Giải thưởng được trao tặng dành cho những doanh nghiệp có đóng góp xuất sắc trong lĩnh vực công nghệ thông tin, đổi mới sáng tạo và phát triển bền vững. PhucAn Solutions đã gây ấn tượng mạnh với ban giám khảo qua các dự án tiêu biểu:</p>
      
      <h3>Các dự án nổi bật:</h3>
      <ul>
        <li><strong>Hệ thống Smart Home tích hợp IoT:</strong> Triển khai thành công hơn 500 căn hộ thông minh tại các khu đô thị cao cấp</li>
        <li><strong>Giải pháp năng lượng mặt trời:</strong> Lắp đặt hệ thống điện mặt trời với tổng công suất hơn 2MW</li>
        <li><strong>Kiến trúc xanh:</strong> Thiết kế và thi công 50+ công trình kiến trúc thân thiện môi trường</li>
      </ul>
      
      <p>Ông Nguyễn Văn A, Giám đốc điều hành PhucAn Solutions chia sẻ: "Giải thưởng này là sự ghi nhận cho những nỗ lực không ngừng của toàn thể đội ngũ trong việc mang đến các giải pháp công nghệ tiên tiến, góp phần xây dựng một tương lai bền vững cho cộng đồng."</p>
      
      <p>Với phương châm "Công nghệ vì cuộc sống tốt đẹp hơn", PhucAn Solutions cam kết tiếp tục đầu tư nghiên cứu và phát triển các sản phẩm, dịch vụ chất lượng cao, đáp ứng nhu cầu ngày càng đa dạng của khách hàng.</p>
    `,
    excerpt: 'Với những thành tích xuất sắc trong lĩnh vực công nghệ và năng lượng sạch, PhucAn Solutions vinh dự nhận giải thưởng danh giá...',
    image: '/images/news-featured-1.jpg',
    category: 'Tin nổi bật',
    author: 'PhucAn Solutions',
    date: 'Thứ Hai, 15 tháng 12, 2024',
    views: 2547,
    slug: slug
  });

  const getFallbackRelatedNews = () => [
    {
      id: 2,
      title: 'Xu hướng IoT trong smart home 2024',
      image: '/images/iot-news-1.jpg',
      date: '10/12/2024',
      category: 'IoT',
      slug: 'xu-huong-iot-smart-home-2024'
    },
    {
      id: 3,
      title: 'Lợi ích của năng lượng mặt trời cho gia đình',
      image: '/images/solar-news-1.jpg',
      date: '11/12/2024',
      category: 'Năng lượng',
      slug: 'loi-ich-nang-luong-mat-troi'
    },
    {
      id: 4,
      title: 'Xu hướng kiến trúc xanh 2024',
      image: '/images/arch-news-1.jpg',
      date: '12/12/2024',
      category: 'Kiến trúc',
      slug: 'xu-huong-kien-truc-xanh-2024'
    }
  ];

  // Loading component
  const LoadingSkeleton = () => (
    <div className="loading-skeleton">
      <div className="skeleton-header"></div>
      <div className="skeleton-meta"></div>
      <div className="skeleton-image"></div>
      <div className="skeleton-content">
        <div className="skeleton-line"></div>
        <div className="skeleton-line"></div>
        <div className="skeleton-line short"></div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <main className="news-detail-main">
        <div className="news-detail-container">
          <LoadingSkeleton />
        </div>
      </main>
    );
  }

  if (error && !newsItem) {
    return (
      <main className="news-detail-main">
        <div className="news-detail-container">
          <div className="error-message">
            <h2>❌ Lỗi tải bài viết</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/news')} className="back-btn">
              ← Quay lại danh sách tin tức
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="news-detail-main">
      <div className="news-detail-container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Trang chủ</Link>
          <span>/</span>
          <Link to="/news">Tin tức</Link>
          <span>/</span>
          <span>{newsItem?.title}</span>
        </nav>

        <div className="news-detail-grid">
          {/* Main Content */}
          <article className="news-article">
            {/* Article Header */}
            <header className="article-header">
              <div className="article-meta">
                <span className="category-badge">{newsItem?.category}</span>
                <time className="article-date">{newsItem?.date}</time>
              </div>
              
              <h1 className="article-title">{newsItem?.title}</h1>
              
              <div className="article-info">
                <div className="author-info">
                  <span>Tác giả: <strong>{newsItem?.author}</strong></span>
                </div>
                <div className="article-stats">
                  <span>👁️ {newsItem?.views?.toLocaleString()} lượt xem</span>
                </div>
              </div>
            </header>

            {/* Featured Image */}
            {newsItem?.image && (
              <div className="article-image">
                <img src={getImageUrl(newsItem.image)} alt={newsItem.title} />
              </div>
            )}

            {/* Article Content */}
            <div className="article-content">
              {newsItem?.excerpt && (
                <div className="article-excerpt">
                  <p><em>{newsItem.excerpt}</em></p>
                </div>
              )}
              
              <div 
                className="article-body"
                dangerouslySetInnerHTML={{ __html: newsItem?.content || '' }}
              />
            </div>

            {/* Article Footer */}
            <footer className="article-footer">
              <div className="social-share">
                <h3>Chia sẻ bài viết:</h3>
                <div className="share-buttons">
                  <button className="share-btn facebook">Facebook</button>
                  <button className="share-btn twitter">Twitter</button>
                  <button className="share-btn linkedin">LinkedIn</button>
                  <button className="share-btn copy-link">Sao chép link</button>
                </div>
              </div>
              
              <div className="article-tags">
                <span>Tags: </span>
                <span className="tag">{newsItem?.category}</span>
                <span className="tag">PhucAn Solutions</span>
              </div>
            </footer>
          </article>

          {/* Sidebar */}
          <aside className="news-sidebar">
            {/* Navigation */}
            <div className="sidebar-card">
              <button onClick={() => navigate('/news')} className="back-btn">
                ← Quay lại danh sách tin tức
              </button>
            </div>

            {/* Related News */}
            {relatedNews.length > 0 && (
              <div className="sidebar-card">
                <h3>Tin liên quan</h3>
                <div className="related-news">
                  {relatedNews.map(news => (
                    <Link key={news.id} to={`/news/${news.slug}`} className="related-item">
                      <div className="related-image">
                        <img src={getImageUrl(news.image)} alt={news.title} />
                      </div>
                      <div className="related-content">
                        <h4>{news.title}</h4>
                        <span className="related-date">{news.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Contact CTA */}
            <div className="sidebar-card cta-card">
              <h3>Cần tư vấn?</h3>
              <p>Liên hệ với chúng tôi để được tư vấn miễn phí về các giải pháp công nghệ.</p>
              <Link to="/contact" className="cta-btn">Liên hệ ngay</Link>
            </div>
          </aside>
        </div>
      </div>
      
      {/* Subscription Section */}
      <SubscriptionSection />
    </main>
  );
};

export default NewsDetail; 